import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Send, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface AIJobSearchProps {
  onSearch: (query: string) => void;
  isSearching: boolean;
  reasoning?: string;
  confidence?: number;
  onClose: () => void;
}

const QUICK_QUERIES = [
  'Jobs in München',
  'Gastronomie-Jobs',
  'Vollzeit-Stellen',
  'Führungspositionen',
  'Jobs in meiner Nähe',
];

export const AIJobSearch = ({ 
  onSearch, 
  isSearching, 
  reasoning, 
  confidence,
  onClose 
}: AIJobSearchProps) => {
  const [query, setQuery] = useState('');

  const handleSearch = () => {
    if (query.trim()) {
      onSearch(query);
    }
  };

  const handleQuickQuery = (quickQuery: string) => {
    setQuery(quickQuery);
    onSearch(quickQuery);
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-gradient-to-br from-primary/5 via-background to-secondary/5 rounded-lg border border-primary/20 relative">
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">KI-gestützte Jobsuche</h3>
      </div>

      <p className="text-sm text-muted-foreground">
        Stelle eine Frage in natürlicher Sprache, z.B. "Zeige mir Jobs in der Nähe von München" oder "Welche Vollzeit-Stellen gibt es?"
      </p>

      <div className="flex gap-2">
        <Input
          placeholder="Beschreibe, welche Jobs du suchst..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          disabled={isSearching}
          className="flex-1"
        />
        <Button 
          onClick={handleSearch}
          disabled={isSearching || !query.trim()}
          size="icon"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {QUICK_QUERIES.map((quickQuery) => (
          <Badge
            key={quickQuery}
            variant="outline"
            className="cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => handleQuickQuery(quickQuery)}
          >
            {quickQuery}
          </Badge>
        ))}
      </div>

      {reasoning && (
        <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">KI-Analyse</p>
              <p className="text-sm text-muted-foreground">{reasoning}</p>
              {confidence !== undefined && confidence > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Übereinstimmung:</span>
                  <div className="flex-1 h-2 bg-secondary/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${confidence}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium">{confidence}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isSearching && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary animate-pulse" />
            <span className="text-sm text-muted-foreground">KI analysiert Jobs...</span>
          </div>
        </div>
      )}
    </div>
  );
};
